import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, message, Empty } from 'antd';
import { smsApi } from '../services/api';
import type { Batch, SMS } from '../types';

interface BatchMessagesModalProps {
  visible: boolean;
  batch: Batch | null;
  onClose: () => void;
}

const BatchMessagesModal: React.FC<BatchMessagesModalProps> = ({ visible, batch, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<SMS[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (visible && batch) {
      loadMessages();
    }
  }, [visible, batch, page, pageSize]);

  const loadMessages = async () => {
    if (!batch) return;

    setLoading(true);
    try {
      const data = await smsApi.getByBatch(batch.id, {
        page,
        limit: pageSize,
      });
      setMessages(data.messages || []);
      setTotal(data.total);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
      width: 140,
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: 'Received',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 160,
      render: (text: string) => new Date(text).toLocaleString(),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Messages</div>
          <div style={{ fontSize: 14, color: '#888', fontWeight: 400, marginTop: 4 }}>
            {batch?.name} ({batch?.country})
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
    >
      {total === 0 && !loading ? (
        <Empty
          description={
            <span>
              No messages available for this batch.
              <br />
              <span style={{ color: '#888', fontSize: 12 }}>
                Messages are filtered based on your role and filter settings.
              </span>
            </span>
          }
        />
      ) : (
        <Table
          dataSource={messages}
          columns={columns}
          rowKey="messageId"
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} messages`,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ y: 400 }}
        />
      )}
    </Modal>
  );
};

export default BatchMessagesModal;
